import { useEffect, useRef, useState } from "react";

interface DraftNumberInputProps {
  value: number | string | null | undefined;
  onSave: (raw: string) => void;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  debounceMs?: number;
  onEnter?: () => void;
  id?: string;
}

function toDisplay(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return "";
  return String(Number(value));
}

/**
 * Numeric input that keeps a local draft while typing and only persists on
 * blur or after a pause, instead of firing a request per keystroke.
 */
export function DraftNumberInput({
  value,
  onSave,
  className,
  placeholder,
  autoFocus,
  disabled,
  debounceMs = 700,
  onEnter,
  id,
}: DraftNumberInputProps) {
  const [draft, setDraft] = useState(() => toDisplay(value));
  const dirtyRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const lastSavedRef = useRef(toDisplay(value));
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Adopt server value when we have no unsaved local edits.
  useEffect(() => {
    if (!dirtyRef.current) {
      setDraft(toDisplay(value));
      lastSavedRef.current = toDisplay(value);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const commit = (raw: string) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    dirtyRef.current = false;
    const normalized = raw.trim();
    if (normalized === lastSavedRef.current) return;
    lastSavedRef.current = normalized;
    onSaveRef.current(normalized);
  };

  const handleChange = (raw: string) => {
    setDraft(raw);
    dirtyRef.current = true;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => commit(raw), debounceMs);
  };

  return (
    <input
      id={id}
      type="number"
      inputMode="decimal"
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit((e.target as HTMLInputElement).value);
          onEnter?.();
        }
      }}
      onFocus={(e) => e.target.select()}
      className={className}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
    />
  );
}
